/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useLayoutEffect, useRef, useState } from 'react';

/**
 * The text of the overflow counter tag. Whole thousands round to a `K` suffix
 * - `1500` becomes `2K` - so the counter keeps a bounded width no matter how
 * large the selection grows.
 */
export const formatOverflowCount = (count: number): string =>
  count < 1000 ? `${count}` : `${Math.round(count / 1000)}K`;

/**
 * Every text the counter could show at its widest for a selection of `total`
 * options. K formatting makes the width non-monotonic - "+999" is wider than
 * "+1K" - so the reserve has to measure both the widest sub-thousand text and
 * the full count rather than the full count alone.
 */
export const getOverflowCounterMeasureTexts = (total: number): string[] =>
  Array.from(
    new Set([
      formatOverflowCount(Math.min(total, 999)),
      formatOverflowCount(total),
    ]),
  );

type ComboboxOverflowMeasurements = {
  /** Widths of every selected tag, in selection order. */
  tagWidths: number[];
  /** Width of the widest counter tag the current selection could need. */
  counterWidth: number;
  /** Content width of the wrapping value container. */
  containerWidth: number;
  /** Column gap between tags. */
  gap: number;
  /** Maximum number of rows the tags may wrap into. */
  maxRows: number;
};

/**
 * How many tags fit within `maxRows` rows of the value container, mirroring
 * the greedy line breaking of `flex-wrap`. When they all fit, the count is the
 * number of tags and no counter is needed; otherwise the result leaves room
 * for the counter tag on the last permitted row.
 */
export const computeVisibleTagCount = ({
  tagWidths,
  counterWidth,
  containerWidth,
  gap,
  maxRows,
}: ComboboxOverflowMeasurements): number => {
  // An unmeasurable container - hidden, or not laid out yet - must not
  // collapse the whole selection into a counter.
  if (containerWidth <= 0) {
    return tagWidths.length;
  }

  const rowLimit = Math.max(1, Math.floor(maxRows));
  const boundedCounterWidth = Math.min(counterWidth, containerWidth);
  // With more than one tag a counter may be needed, so the stylesheet caps
  // every tag at the container width minus room for the counter, and the
  // simulation must clamp with the same cap to match the rendered widths.
  // Extremely narrow containers where the cap would leave no room for a tag
  // fall back to capping at the container width alone.
  const counterReservedCap = containerWidth - gap - boundedCounterWidth;
  const tagCap =
    tagWidths.length > 1 && counterReservedCap > 0
      ? counterReservedCap
      : containerWidth;
  const widths = tagWidths.map((width) => Math.min(width, tagCap));
  // Row and remaining row width after each prefix of tags, so every candidate
  // cutoff can be checked without resimulating.
  const placements: { row: number; rowWidth: number }[] = [];
  let row = 1;
  let rowWidth = 0;

  widths.forEach((width, index) => {
    if (index === 0) {
      rowWidth = width;
    } else if (rowWidth + gap + width <= containerWidth) {
      rowWidth += gap + width;
    } else {
      row += 1;
      rowWidth = width;
    }

    placements.push({ row, rowWidth });
  });

  if (row <= rowLimit) {
    return widths.length;
  }

  for (let count = widths.length - 1; count >= 1; count -= 1) {
    const placement = placements[count - 1];
    const counterRow =
      placement.rowWidth + gap + boundedCounterWidth <= containerWidth
        ? placement.row
        : placement.row + 1;

    if (counterRow <= rowLimit) {
      return count;
    }
  }

  // Not even one tag leaves room for the counter, so the counter stands alone.
  return 0;
};

type UseComboboxMultiValueOverflowOptions = {
  /** Undefined leaves the selection unbounded and skips all measuring. */
  maxRows: number | undefined;
  /** Number of selected options rendered as tags. */
  tagCount: number;
  /**
   * Anything that changes tag widths without changing the container size, such
   * as the selection itself or the control size, so those changes re-measure.
   */
  measureKey: string;
};

/**
 * Caps the rows of selected tags in a multi select control. All tags stay
 * rendered; the ones past `visibleCount` are taken out of the layout by the
 * stylesheet but stay measurable, so the computation is stable across passes.
 * Width comes from a `ResizeObserver` on the value container, and everything
 * else from `measureKey`.
 */
export const useComboboxMultiValueOverflow = ({
  maxRows,
  tagCount,
  measureKey,
}: UseComboboxMultiValueOverflowOptions) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // null shows every tag, which is also the server-rendered state.
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const isLimited = maxRows !== undefined && tagCount > 0;

  const measure = () => {
    const container = containerRef.current;

    if (maxRows === undefined || container === null) {
      setVisibleCount(null);
      return;
    }

    const tags = Array.from(
      container.querySelectorAll<HTMLElement>(
        ':scope > .ndl-combobox-multi-value',
      ),
    );
    // With K formatting the widest counter is not always the one for the full
    // count - "+999" is wider than "+1K" - so every candidate is rendered as a
    // measure tag and the widest one sets the reserve.
    const counters = Array.from(
      container.querySelectorAll<HTMLElement>(
        ':scope > .ndl-combobox-multi-value-measure',
      ),
    );

    if (tags.length === 0 || counters.length === 0) {
      setVisibleCount(null);
      return;
    }

    const counterWidth = Math.max(
      ...counters.map((counter) => counter.getBoundingClientRect().width),
    );
    const gap =
      Number.parseFloat(window.getComputedStyle(container).columnGap) || 0;

    // The stylesheet caps every tag at the container width minus this reserve,
    // so a tag too wide to sit next to the counter shrinks instead of
    // collapsing. Set before the tag rects are read, so the forced layout
    // below applies it and the widths reflect the cap. Rounded up plus one
    // pixel of slack, so subpixel layout quantization can never make a capped
    // tag plus the counter overflow the row.
    container.style.setProperty(
      '--ndl-combobox-counter-reserve',
      `${Math.ceil(gap + counterWidth) + 1}px`,
    );

    // Fractional widths, so rounding never claims room the browser would wrap.
    const nextVisibleCount = computeVisibleTagCount({
      containerWidth: container.getBoundingClientRect().width,
      counterWidth,
      gap,
      maxRows,
      tagWidths: tags.map((tag) => tag.getBoundingClientRect().width),
    });

    setVisibleCount(nextVisibleCount >= tags.length ? null : nextVisibleCount);
  };

  const measureRef = useRef(measure);

  useLayoutEffect(() => {
    measureRef.current = measure;
    measure();
    // measure() reads the DOM, so it depends on everything the key encodes
    // rather than on reactive values of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLimited, maxRows, tagCount, measureKey]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!isLimited || container === null) {
      return;
    }

    // Hiding tags only changes the container height, and the recomputation is
    // idempotent, so the observer settles after a single extra pass.
    const observer = new ResizeObserver(() => {
      measureRef.current();
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [isLimited]);

  return { containerRef, visibleCount };
};
